import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface HakEdisRow {
  id: string;
  ad_soyad: string;
  hakedis_orani: number;
  toplamTahsilat: number;
  ogretmenHakedisi: number;
  odenenTutar: number;
  bekleyenTutar: number;
  sonOdemeTarihi: string | null;
  durum: string;
}

interface HakEdisOdeme {
  id: string;
  odeme_tarihi: string;
  odeme_tutari: number;
  aciklama: string | null;
  odeme_durumu: string | null;
}

interface TeacherPayment {
  id: string;
  tutar: number;
  dekont_tarihi: string;
  ogretmen_payi: number | null;
  hakedis_orani: number | null;
  ogrenci?: { ogrenci_adi: string };
}

export default function HakEdislerPage() {
  const [rows, setRows] = useState<HakEdisRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailPayments, setDetailPayments] = useState<HakEdisOdeme[]>([]);
  const [teacherPayments, setTeacherPayments] = useState<TeacherPayment[]>([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ date: '', amount: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    const { data: teachers } = await supabase.from('egtpanel_ogretmenler').select('id, ad_soyad, hakedis_orani').eq('aktif', true).order('ad_soyad');
    if (!teachers) { setLoading(false); return; }

    const result: HakEdisRow[] = await Promise.all(teachers.map(async t => {
      const [{ data: payments }, { data: hakOdemeleri }] = await Promise.all([
        supabase.from('egtpanel_odemeler').select('tutar, ogretmen_payi').eq('ogretmen_id', t.id).eq('yonetici_onay', true),
        supabase.from('egtpanel_hak_edis_odemeleri').select('odeme_tutari, odeme_tarihi').eq('ogretmen_id', t.id).order('odeme_tarihi', { ascending: false }),
      ]);
      const oran = t.hakedis_orani || 50;
      const toplamTahsilat = (payments || []).reduce((a, p) => a + Number(p.tutar), 0);
      const ogretmenHakedisi = (payments || []).reduce((a, p) => a + Number(p.ogretmen_payi || (p.tutar * oran / 100)), 0);
      const odenenTutar = (hakOdemeleri || []).reduce((a, p) => a + Number(p.odeme_tutari), 0);
      const bekleyenTutar = Math.max(0, ogretmenHakedisi - odenenTutar);
      const sonOdeme = hakOdemeleri?.[0]?.odeme_tarihi || null;
      const durum = bekleyenTutar === 0 ? 'Güncel' : 'Bekliyor';
      return { id: t.id, ad_soyad: t.ad_soyad, hakedis_orani: oran, toplamTahsilat, ogretmenHakedisi, odenenTutar, bekleyenTutar, sonOdemeTarihi: sonOdeme, durum };
    }));
    setRows(result);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedRow = rows.find(r => r.id === selectedId);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setShowPayModal(false);
    const [{ data: odemeleri }, { data: tPay }] = await Promise.all([
      supabase.from('egtpanel_hak_edis_odemeleri').select('*').eq('ogretmen_id', id).order('odeme_tarihi', { ascending: false }),
      supabase.from('egtpanel_odemeler').select('id, tutar, dekont_tarihi, ogretmen_payi, hakedis_orani, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', id).eq('yonetici_onay', true),
    ]);
    setDetailPayments(odemeleri || []);
    setTeacherPayments((tPay || []) as TeacherPayment[]);
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    await supabase.from('egtpanel_hak_edis_odemeleri').insert({
      ogretmen_id: selectedId,
      odeme_tarihi: payForm.date,
      odeme_tutari: Number(payForm.amount),
      aciklama: payForm.description || null,
      odeme_durumu: 'Ödendi',
    });
    setSaving(false);
    setShowPayModal(false);
    setPayForm({ date: '', amount: '', description: '' });
    await fetchData();
    if (selectedId) openDetail(selectedId);
  };

  const totalEarning = rows.reduce((a, r) => a + r.ogretmenHakedisi, 0);
  const totalPaid = rows.reduce((a, r) => a + r.odenenTutar, 0);
  const totalPending = rows.reduce((a, r) => a + r.bekleyenTutar, 0);

  const statusColors: Record<string, string> = {
    Güncel: 'bg-green-100 text-green-700',
    Bekliyor: 'bg-yellow-100 text-yellow-700',
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Öğretmen Hak Edişleri</h1>
        <p className="text-slate-500 text-sm mt-1">Öğretmen hak ediş hesaplarını takip edin ve ödemeleri yönetin</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Toplam Hak Ediş', val: `₺${totalEarning.toLocaleString('tr-TR')}`, color: 'text-[#1e3a5f]', bg: 'bg-[#1e3a5f]/5', icon: 'ri-money-dollar-circle-line' },
          { label: 'Ödenen', val: `₺${totalPaid.toLocaleString('tr-TR')}`, color: 'text-green-700', bg: 'bg-green-50', icon: 'ri-checkbox-circle-line' },
          { label: 'Bekleyen', val: `₺${totalPending.toLocaleString('tr-TR')}`, color: 'text-orange-600', bg: 'bg-orange-50', icon: 'ri-time-line' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-5 flex items-center gap-4`}>
            <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl shrink-0">
              <i className={`${s.icon} ${s.color} text-xl`} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Öğretmen', 'Toplam Tahsilat', 'Oran', 'Hak Ediş', 'Ödenen', 'Bekleyen', 'Son Ödeme', 'Durum', 'İşlem'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16">
                  <i className="ri-user-star-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Öğretmen kaydı bulunamadı</p>
                </td></tr>
              ) : rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1e3a5f] text-white text-sm font-bold shrink-0">{r.ad_soyad.charAt(0)}</div>
                      <span className="font-medium text-slate-800">{r.ad_soyad}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">₺{r.toplamTahsilat.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">%{r.hakedis_orani}</td>
                  <td className="px-4 py-3 font-semibold text-[#1e3a5f] whitespace-nowrap">₺{r.ogretmenHakedisi.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-green-700 font-medium whitespace-nowrap">₺{r.odenenTutar.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={r.bekleyenTutar > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>₺{r.bekleyenTutar.toLocaleString('tr-TR')}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.sonOdemeTarihi || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[r.durum] || 'bg-slate-100 text-slate-500'}`}>{r.durum}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap flex gap-2">
                    <button onClick={() => openDetail(r.id)} className="text-[#1e3a5f] text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5 transition-colors cursor-pointer">Detay</button>
                    {r.bekleyenTutar > 0 && (
                      <button onClick={() => { setSelectedId(r.id); setShowPayModal(true); }} className="text-green-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-green-200 hover:bg-green-50 transition-colors cursor-pointer">Öde</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedId && !showPayModal && selectedRow && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{selectedRow.ad_soyad} — Hak Ediş Detayı</h2>
                <p className="text-sm text-slate-500">Hak Ediş Oranı: %{selectedRow.hakedis_orani}</p>
              </div>
              <button onClick={() => setSelectedId(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Toplam Tahsilat', val: `₺${selectedRow.toplamTahsilat.toLocaleString('tr-TR')}` },
                  { label: `Hak Ediş (%${selectedRow.hakedis_orani})`, val: `₺${selectedRow.ogretmenHakedisi.toLocaleString('tr-TR')}` },
                  { label: 'Platform Payı', val: `₺${(selectedRow.toplamTahsilat - selectedRow.ogretmenHakedisi).toLocaleString('tr-TR')}` },
                  { label: 'Ödenen', val: `₺${selectedRow.odenenTutar.toLocaleString('tr-TR')}`, color: 'text-green-700' },
                  { label: 'Bekleyen', val: `₺${selectedRow.bekleyenTutar.toLocaleString('tr-TR')}`, color: 'text-orange-600' },
                  { label: 'Son Ödeme', val: selectedRow.sonOdemeTarihi || '—' },
                ].map(s => (
                  <div key={s.label} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-400">{s.label}</p>
                    <p className={`text-base font-bold ${s.color || 'text-slate-800'}`}>{s.val}</p>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Onaylı Tahsilatlar</h3>
                {teacherPayments.length === 0 ? (
                  <p className="text-slate-400 text-sm">Onaylı ödeme yok</p>
                ) : teacherPayments.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-slate-50">
                    <div>
                      <span className="text-sm font-medium text-slate-700">{(p.ogrenci as {ogrenci_adi: string} | undefined)?.ogrenci_adi || '—'}</span>
                      <span className="text-xs text-slate-400 ml-2">{p.dekont_tarihi}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-slate-800">₺{Number(p.tutar).toLocaleString('tr-TR')}</div>
                      <div className="text-xs text-green-600">Öğretmen: ₺{Number(p.ogretmen_payi || 0).toLocaleString('tr-TR')}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Ödeme Geçmişi</h3>
                {detailPayments.length === 0 ? (
                  <p className="text-slate-400 text-sm">Ödeme kaydı yok</p>
                ) : detailPayments.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-slate-50">
                    <div>
                      <span className="text-sm font-medium text-slate-700">{p.odeme_tarihi}</span>
                      {p.aciklama && <span className="text-xs text-slate-400 ml-2">{p.aciklama}</span>}
                    </div>
                    <span className="text-sm font-semibold text-green-700">₺{Number(p.odeme_tutari).toLocaleString('tr-TR')}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setSelectedId(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">Kapat</button>
                {selectedRow.bekleyenTutar > 0 && (
                  <button onClick={() => setShowPayModal(true)} className="flex-1 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 cursor-pointer">
                    <i className="ri-check-line mr-1" /> Ödeme Yap
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedRow && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Ödeme Yap</h2>
              <button onClick={() => { setShowPayModal(false); setSelectedId(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handlePay} className="p-6 space-y-4">
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-sm font-medium text-amber-800">{selectedRow.ad_soyad}</p>
                <p className="text-xs text-amber-600 mt-0.5">Bekleyen tutar: ₺{selectedRow.bekleyenTutar.toLocaleString('tr-TR')}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ödeme Tarihi *</label>
                <input required type="date" value={payForm.date} onChange={e => setPayForm(p => ({...p, date: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ödeme Tutarı (₺) *</label>
                <input required type="number" value={payForm.amount} onChange={e => setPayForm(p => ({...p, amount: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama</label>
                <textarea value={payForm.description} onChange={e => setPayForm(p => ({...p, description: e.target.value}))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowPayModal(false); setSelectedId(null); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 cursor-pointer">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : <><i className="ri-check-line mr-1" /> Ödeme Yapıldı</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}